import React from 'react';
import { Pencil, ArrowDownUp, Trash, Plus } from 'react-bootstrap-icons';
import { formatInventoryUnit } from '../../helpers/global';
import { isAlphaNumericKey, isNavigationKey, keyCodes } from '@silevis/reactgrid';
import UnderlineButton from './UnderlineButton';

class DealboardInventoryCellTemplate {
  getCompatibleCell(uncertainCell) {
    const {
      inventoryUnits = [],
      onEditClick = () => {},
      onRemoveClick = () => {},
      onLinkNew = () => {},
      onChangeClick = () => {},
      nonEditable = false,
      isLoading = false
    } = uncertainCell;

    return {
      inventoryUnits,
      onEditClick,
      onRemoveClick,
      onChangeClick,
      onLinkNew,
      nonEditable,
      isLoading,
      text: uncertainCell.text || ''
    };
  }

  handleKeyDown(cell, keyCode, ctrl, shift, alt) {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) return { cell, enableEditMode: false };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  update(cell, cellToMerge) {
    return { ...cell, ...cellToMerge };
  }

  render(cell) {
    const { inventoryUnits, onEditClick, onChangeClick, onRemoveClick, onLinkNew, isLoading } = cell;

    return (
      <DealboardInventoryCellRenderer
        inventoryUnits={inventoryUnits}
        onEditClick={onEditClick}
        onChangeClick={onChangeClick}
        onRemoveClick={onRemoveClick}
        onLinkNew={onLinkNew}
        isLoading={isLoading}
      />
    );
  }
}

const DealboardInventoryCellRenderer = ({
  inventoryUnits,
  onEditClick,
  onChangeClick,
  onRemoveClick,
  onLinkNew,
  isLoading
}) => {
  const hasUnits = Array.isArray(inventoryUnits) && inventoryUnits.length > 0;

  const containerStyle = {
    height: '100%',
    backgroundColor: isLoading ? '#fff3cd' : 'transparent',
    opacity: isLoading ? 0.6 : 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: isLoading ? 'default' : 'pointer',
    width: '100%'
  };

  const unitRowStyle = index => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.25rem',
    borderTop: index === 0 ? 'none' : '1px solid #e9ecef',
    padding: '5px'
  });

  const iconStyle = {
    cursor: isLoading ? 'not-allowed' : 'pointer',
    color: '#000000',
    marginLeft: '0.5rem'
  };

  const trashStyle = {
    cursor: isLoading ? 'not-allowed' : 'pointer',
    color: '#dc3545',
    marginLeft: '0.5rem'
  };

  return (
    <div
      className="dealboard-inventory-cell"
      style={containerStyle}
      onPointerDown={e => e.stopPropagation()}
      onKeyDown={e => {
        if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) {
          e.stopPropagation();
        }
      }}
    >
      <div className="d-flex align-items-center gap-2 justify-content-center" style={{ width: '100%' }}>
        {hasUnits && (
          <div style={{ width: '100%', flex: 1 }}>
            {inventoryUnits.map((unit, idx) => {
              const formatted = formatInventoryUnit(unit) || '';
              return (
                <div key={unit._id || idx} style={unitRowStyle(idx)}>
                  <p style={{ fontSize: '12px', margin: 0 }}>{formatted}</p>
                  <div style={{ display: 'flex', alignItems: 'center' }} className="gap-2">
                    <Pencil
                      size={14}
                      style={iconStyle}
                      onClick={e => {
                        e.stopPropagation();
                        if (!isLoading) onEditClick(unit);
                      }}
                    />
                    <ArrowDownUp
                      size={14}
                      style={iconStyle}
                      onClick={e => {
                        e.stopPropagation();
                        if (!isLoading) onChangeClick(unit?._id);
                      }}
                    />
                    <Trash
                      size={14}
                      style={trashStyle}
                      onClick={e => {
                        e.stopPropagation();
                        if (!isLoading) onRemoveClick(unit?._id);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ minWidth: 'fit-content' }}>
          <UnderlineButton
            variant="success"
            Icon={() => <Plus size={14} />}
            text="Link New"
            fontSize="small"
            onClick={e => {
              e.stopPropagation();
              if (!isLoading) onLinkNew();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const dealboardInventoryCellTemplate = new DealboardInventoryCellTemplate();
